import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import MyLocationOutlinedIcon from "@mui/icons-material/MyLocationOutlined";
import { getLocation, hasCoords, readStoredLocation } from "./Navigator/Navigator";
import { loadGoogleMaps, writeStoredLocation } from "../utils/googleLocation";
import { setLocation } from "../features/authSlice";

const isLocationFreePath = (pathname = "") =>
  String(pathname).includes("/payment/callback");

const LocationMap = ({ location }) => {
  const mapRef = useRef(null);
  const mapObj = useRef(null);

  useEffect(() => {
    if (!hasCoords(location) || !mapRef.current) return undefined;
    let cancelled = false;
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !mapRef.current) return;
        const center = {
          lat: Number(location.latitude),
          lng: Number(location.longitude),
        };
        if (!mapObj.current) {
          mapObj.current = {
            map: new maps.Map(mapRef.current, {
              center,
              zoom: 16,
              disableDefaultUI: true,
              zoomControl: true,
            }),
            marker: new maps.Marker({ position: center, map: null }),
          };
          mapObj.current.marker.setMap(mapObj.current.map);
        } else {
          mapObj.current.map.setCenter(center);
          mapObj.current.marker.setPosition(center);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [location]);

  if (!hasCoords(location)) return null;
  return (
    <Box
      ref={mapRef}
      sx={{
        width: "100%",
        height: 220,
        borderRadius: 2,
        mb: 2,
        overflow: "hidden",
        border: "1px solid var(--border-color, #E2E8F0)",
      }}
    />
  );
};

const LocationGate = ({ children }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const location = useSelector((state) => state.auth.location);
  const [status, setStatus] = useState(
    hasCoords(location) || hasCoords(readStoredLocation()) ? "ready" : "checking"
  );
  const [error, setError] = useState("");

  const applyLocation = useCallback(
    (loc) => {
      writeStoredLocation(loc);
      dispatch(setLocation(loc));
      setStatus("ready");
      setError("");
    },
    [dispatch]
  );

  const requestLocation = useCallback(async () => {
    setStatus("checking");
    setError("");
    try {
      const loc = await getLocation({ silent: true });
      if (!hasCoords(loc)) {
        throw new Error("Latitude and longitude were not returned");
      }
      applyLocation(loc);
    } catch (err) {
      setStatus("blocked");
      setError(
        err?.userMessage ||
          err?.message ||
          "Allow location access, then tap the button below."
      );
    }
  }, [applyLocation]);

  useEffect(() => {
    const cached = location || readStoredLocation();
    if (hasCoords(cached)) {
      applyLocation(cached);
      return;
    }
    requestLocation();
    // Resolve once on app open; user can retry from the gate button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasCoords(location)) return undefined;
    const timer = setInterval(() => {
      getLocation({ silent: true })
        .then((loc) => {
          if (hasCoords(loc)) dispatch(setLocation(loc));
        })
        .catch(() => {});
    }, 10 * 60 * 1000);
    return () => clearInterval(timer);
  }, [dispatch, location]);

  if (isLocationFreePath(pathname)) {
    return children;
  }

  if (status === "checking" && !hasCoords(location)) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
          bgcolor: "var(--bg-color, #fff)",
        }}
      >
        <CircularProgress />
        <Typography>Detecting your location with Google Maps…</Typography>
      </Box>
    );
  }

  if (!hasCoords(location) || status === "blocked") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          bgcolor: "var(--bg-color, #fff)",
        }}
      >
        <Box
          sx={{
            maxWidth: 480,
            width: "100%",
            textAlign: "center",
            p: 3,
            borderRadius: "var(--radius-lg, 12px)",
            border: "1px solid var(--border-color, #E2E8F0)",
            boxShadow: "var(--shadow-md)",
            bgcolor: "var(--surface-color, #fff)",
          }}
        >
          <MyLocationOutlinedIcon
            sx={{ fontSize: 56, color: "var(--primary-color, #02026E)", mb: 1 }}
          />
          {hasCoords(location) ? <LocationMap location={location} /> : null}
          <Typography variant="h5" fontWeight={700} mb={1}>
            Location required
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={2}>
            {error ||
              "Allow location when the browser asks. Google Maps is used to pin your live latitude and longitude."}
          </Typography>
          <Button variant="contained" onClick={requestLocation}>
            Allow location and continue
          </Button>
        </Box>
      </Box>
    );
  }

  return children;
};

export default LocationGate;
